import { useState, useEffect, useRef } from "react";
import { apiCall } from "../../../utils/apiCall";
import {
  Button,
  Drawer,
  Tabs,
  TabsProps,
  Form,
  Select,
  Input,
  message,
} from "antd";
import { AdvisorDataType } from "./types";
import { Colors } from "../../../constants/Colors";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import { BranchInfoType } from "../leadsManagement/types";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import fetchAdvisorsData from "../leadsManagement/utils/fetchAdvisorsData";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { Dispatch, SetStateAction } from "react";
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { ServiceTypes } from "../../../types/types";
import { convertToBranchOptions } from "../leadsManagement/utils/convertToAdvisorTreeSelectOptions";
import TabContent from "./TabContent";
import "./AdvisorManagement.scss";

interface AdvisorManagementProps {
  setSelectedService: Dispatch<SetStateAction<ServiceTypes>>;
  setSelectSubItem: Dispatch<SetStateAction<string | undefined>>;
}

const AdvisorManagement = ({
  setSelectedService,
  setSelectSubItem,
}: AdvisorManagementProps) => {
  const [activeKey, setActiveKey] = useState("1");
  const [generalData, setGeneralData] = useState<AdvisorDataType[]>([]);
  const [headQuarterData, setHeadQuarterData] = useState<AdvisorDataType[]>([]);
  const [resignedData, setResignedData] = useState<AdvisorDataType[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [branchSelectOptions, setBranchSelectOptions] = useState<any>([]);
  const [advisors, setAdvisors] = useState<BranchInfoType[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const [showAddressWindow, setShowAddressWindow] = useState(false);
  const [form] = Form.useForm();
  const header = {
    headers: {
      "x-api-key": process.env.REACT_APP_X_API_KEY,
    },
  };
  const currentUser = useReactiveVar(__currentUser__);
  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const fetchData = async () => {
    const data = await apiCall({
      method: "GET",
      endPoint: process.env.REACT_APP_SALES_ADMIN + "/advisors/admin",
      header: header,
    });
    if (data) {
      setGeneralData(data.general);
      setHeadQuarterData(data.headquarters);
      setResignedData(data.resigned);
    }
  };

  const handleSubmit = async () => {
    const {
      branch,
      companyNumber,
      name,
      englishName,
      phoneNumber,
      email,
      zip,
      address1,
      address2,
    } = form.getFieldsValue();

    const requiredFields = {
      branchCode: branch || "",
      name: name || "",
      phoneNumber: phoneNumber || "",
    };

    const optionalFields = {
      englishName,
      email,
      zipCode: zip,
      address1,
      address2,
    };

    const filteredOptionalFields = Object.fromEntries(
      Object.entries(optionalFields).filter(
        ([_, value]) => value !== undefined && value !== ""
      )
    );

    const payload = { ...requiredFields, ...filteredOptionalFields };

    const data = await apiCall({
      method: "POST",
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + `/advisors/admin/${companyNumber}`,
      header: header,
      payload: payload,
    });
    if (data.status !== "SUCCESS") {
      message.error(renderAntDMessageConfig(data.response.data.status));
      return;
    } else {
      message.success(
        renderAntDMessageConfig(
          "신규 어드바이저를 등록하였습니다. 정식가입은 활동 승인 이후 이루어집니다."
        )
      );
      closeDrawer();
      fetchData();
    }
  };

  useEffect(() => {
    const loadDaumPostcode = () => {
      new window.daum.Postcode({
        oncomplete: function (data: any) {
          form.setFieldsValue({
            zip: data?.zonecode,
            address1: data?.address,
          });
          setShowAddressWindow(false);
        },
        width: "100%",
        height: "100%",
      }).embed(modalRef.current);
    };

    if (!window.daum || !window.daum.Postcode) {
      const script = document.createElement("script");
      script.src =
        "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      script.onload = loadDaumPostcode;
      document.body.appendChild(script);
    } else {
      loadDaumPostcode();
    }
  }, [showAddressWindow]);

  const closeDrawer = () => {
    setDrawerOpen(false);
    setShowAddressWindow(false);
    form.resetFields();
  };

  const onChange = (key: string) => {
    setActiveKey(key);
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "사번대장",
      children: (
        <TabContent type="general" data={generalData} fetchData={fetchData} />
      ),
    },
    {
      key: "2",
      label: "본사",
      children: (
        <TabContent type="head" data={headQuarterData} fetchData={fetchData} />
      ),
    },
    {
      key: "3",
      label: "퇴사자",
      children: (
        <TabContent type="resigned" data={resignedData} fetchData={fetchData} />
      ),
    },
  ];

  useEffect(() => {
    fetchData();
    fetchAdvisorsData(header, setAdvisors);
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("시스템 관리");
  }, []);

  useEffect(() => {
    if (advisors.length > 0) {
      setBranchSelectOptions(convertToBranchOptions(advisors));
    }
  }, [advisors]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "System Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="advisorManagementWrapper">
      <div className="flex justify-between items-center px-6 py-4 bg-white sticky-top">
        <div className="flex items-center gap-2 text-xl font-semibold text-textPrimary">
          <h2>어드바이저 관리</h2>
        </div>
        <Button
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          신규 AD 등록
        </Button>
      </div>
      <Tabs
        activeKey={activeKey}
        items={items}
        onChange={onChange}
        tabBarStyle={{
          paddingLeft: 24,
          backgroundColor: Colors.white,
          height: 50,
        }}
      />
      <Drawer
        title="신규 어드바이저 등록"
        id="advisorManagementDrawer"
        open={drawerOpen}
        onClose={closeDrawer}
        className="relative"
        footer={
          <div className="flex justify-end">
            <Button onClick={closeDrawer}>취소</Button>
            <Button
              type="primary"
              className="ml-1"
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    handleSubmit();
                  })
                  .catch((e) => console.log(e));
              }}
            >
              등록
            </Button>
          </div>
        }
      >
        <Form form={form} name="createAdvisorForm" layout="vertical">
          <Form.Item
            name="branch"
            label="지사"
            rules={[
              { required: true, message: "지사 선택은 필수 항목입니다." },
            ]}
          >
            <Select
              options={branchSelectOptions}
              placeholder="지사를 선택해주세요."
            />
          </Form.Item>
          <Form.Item
            name="companyNumber"
            label="사번"
            rules={[{ required: true, message: "사번 입력은 필수입니다." }]}
          >
            <Input placeholder="사번을 입력해주세요." />
          </Form.Item>
          <Form.Item
            name="name"
            label="이름"
            rules={[{ required: true, message: "이름 입력은 필수입니다." }]}
          >
            <Input placeholder="이름을 입력해주세요." />
          </Form.Item>
          <Form.Item name="englishName" label="영어이름">
            <Input placeholder="영어이름을 입력해주세요." />
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            label="휴대폰번호"
            rules={[
              { required: true, message: "휴대폰번호 입력은 필수입니다." },
            ]}
          >
            <Input placeholder="휴대폰번호를 입력해주세요." />
          </Form.Item>
          <Form.Item name="email" label="이메일">
            <Input placeholder="example@gmail.com" />
          </Form.Item>
          <Form.Item label="주소">
            <div className="flex justify-between mb-2 h-32px w-full">
              <Form.Item className="w-4/5" name="zip">
                <Input disabled placeholder="우편번호" />
              </Form.Item>
              <Form.Item className="ms-2">
                <Button
                  icon={<SearchOutlined />}
                  onClick={() => {
                    setShowAddressWindow(!showAddressWindow);
                  }}
                >
                  주소찾기
                </Button>
              </Form.Item>
            </div>
            <Form.Item name="address1" className="mt-0 mb-2">
              <Input disabled placeholder="주소" />
            </Form.Item>
            <Form.Item name="address2" className="mt-0">
              <Input placeholder="상세주소" />
            </Form.Item>
          </Form.Item>
        </Form>
        {showAddressWindow && (
          <div className="address-modal">
            <div className="flex justify-between items-center title">
              <p>주소검색</p>
              <div
                onClick={() => {
                  setShowAddressWindow(false);
                }}
              >
                <CloseOutlined />
              </div>
            </div>
            <div ref={modalRef} className="address-modal-2" />
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default AdvisorManagement;
